import React, { useState, useEffect } from 'react';

import LoadingSvg from '@/components/common/LoadingSvg';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';

import { EasyPayScheduleResponse, useBalanceHistory, MonthlyBalance } from '../hooks/useEasyPaySchedule';

import { EasyPayMonthlyBreakdownDialog } from './EasyPayMonthlyBreakdownDialog';

interface BalanceRemainingProps {
    data?: EasyPayScheduleResponse;
    isOpen?: boolean;
}

const EasyPayBalanceRemaining: React.FC<BalanceRemainingProps> = ({ isOpen = false }) => {
    const [selectedMonth, setSelectedMonth] = useState<MonthlyBalance['BillMonth'] | null>(null);
    const [selectedDisplayMonth, setSelectedDisplayMonth] = useState<MonthlyBalance['BillMonth'] | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const { data, isLoading, error } = useBalanceHistory(isOpen);

    // created formatted summed total of all data.AmountDue
    const total = data ? data.reduce((sum, item) => sum + item.AmountDue, 0) : 0;
    const formattedTotal = total.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const handleProgressClick = (selectedMonth, selectedDisplayMonth) => {
        setSelectedMonth(selectedMonth);
        setSelectedDisplayMonth(selectedDisplayMonth);
        setIsDialogOpen(true);
    };

    if (isLoading) {
        return (
            <div className="flex min-h-96 items-center justify-center">
                <div className="flex flex-col items-center">
                    <LoadingSvg />
                    Loading Easy Pay Balance Remaining
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex min-h-96 items-center justify-center">
                <div className="flex flex-col items-center">Error Loading EasyPay</div>
            </div>
        );
    }

    if (!data || data.length === 0) {
        return <div className="w-full py-8 text-center text-sm text-gray-600">No balance history available.</div>;
    }

    return (
        <div className="w-full">
            <div className="grid max-w-[438px] grid-cols-[1fr_1.1fr] text-xs font-light leading-6 md:text-sm md:leading-6">
                {/* Mobile/Tablet Headers */}
                <div
                    style={{ borderBottom: '1px solid #000' }}
                    className="py-2 pr-1 font-bold uppercase leading-5 text-black"
                >
                    Month
                </div>
                <div
                    style={{ borderBottom: '1px solid #000' }}
                    className="py-2 pr-1 text-right font-bold uppercase leading-5 text-black"
                >
                    Amount Remaining
                </div>

                {/* iterate over data to display monthly balances */}
                {data.map((balance) => (
                    <React.Fragment key={balance.BillMonth}>
                        <div className="py-2 pr-1 font-light leading-5 text-black">
                            <button
                                type="button"
                                className="cursor-pointer underline"
                                onClick={() => handleProgressClick(balance.BillMonth, balance.DisplayBillMonth)}
                            >
                                {balance.DisplayBillMonth}
                            </button>
                        </div>
                        <div className="py-2 pr-1 text-right leading-5 text-black">
                            $
                            {balance.AmountDue.toLocaleString('en-CA', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            })}
                        </div>
                    </React.Fragment>
                ))}
                <div
                    className="mt-4 py-2 font-bold uppercase leading-5 text-black"
                    style={{ borderBottom: '1px solid #000', borderTop: '1px solid #000' }}
                >
                    Total:
                </div>
                <div
                    className="mt-4 py-2 text-right font-bold uppercase leading-5 text-black"
                    style={{ borderBottom: '1px solid #000', borderTop: '1px solid #000' }}
                >
                    ${formattedTotal}
                </div>
            </div>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent fullscreenOnMobile className="sm:rounded-sm">
                    {selectedMonth && (
                        <EasyPayMonthlyBreakdownDialog yearMonth={selectedMonth} displayMonth={selectedDisplayMonth} />
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default EasyPayBalanceRemaining;
